import { AlertCircle, ArrowUpRight, Clock, RefreshCw } from "lucide-react";
import { useState } from "react";

import { OrganizationScopePicker } from "@/modules/dashboard/payments-config/components/OrganizationScopePicker";
import { useOrganizationsCatalog } from "@/modules/dashboard/payments-config/hooks/useOrganizationsCatalog";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { WalletDebitDetailDrawer } from "../components/WalletDebitDetailDrawer";
import { NewWalletDebitDialog } from "../components/WalletDebitDialog";
import { WalletDebitsList } from "../components/WalletDebitsList";
import { useWalletDebits } from "../hooks/useWalletDebits";
import type { WalletDebit } from "../types/walletDebits.types";

export default function WalletDebitsPage() {
  const { t } = useTranslations("walletDebits");
  const { t: tBatches } = useTranslations("payoutBatches");
  const catalog = useOrganizationsCatalog();
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [opened, setOpened] = useState<WalletDebit | null>(null);
  const history = useWalletDebits(organizationId);
  const pending = useWalletDebits(organizationId, "RESERVED");
  const reload = () => {
    history.reload();
    pending.reload();
  };

  if (!history.hasAccess) return <NoAccessView />;

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
      <ColocaPageHeader
        title={t("page_title")}
        description={t("page_subtitle")}
        actions={
          <div className="flex items-center gap-2">
            <ColocaIconButton
              icon={RefreshCw}
              variant="ghost"
              size="sm"
              aria-label={t("action_refresh")}
              className={cn((history.isLoading || pending.isLoading) && "[&_svg]:animate-spin")}
              onClick={reload}
            />
            <ColocaButton variant="primary" icon={ArrowUpRight} onClick={() => setCreating(true)}>
              {t("action_new")}
            </ColocaButton>
          </div>
        }
      />

      <OrganizationScopePicker
        id="wallet-debits-scope"
        className="max-w-md"
        value={organizationId}
        onValueChange={setOrganizationId}
        catalog={catalog}
        labels={{
          label: t("filter_organization"),
          placeholder: tBatches("org_picker_placeholder"),
          loading: tBatches("org_picker_loading"),
          empty: tBatches("org_picker_empty"),
          searching: tBatches("org_picker_searching"),
          searchError: tBatches("org_picker_search_error"),
          catalogError: tBatches("org_picker_catalog_error"),
          catalogTruncated: tBatches("org_picker_catalog_truncated"),
          globalOption: t("filter_all_option"),
          globalHint: t("filter_all_hint"),
          orgHint: t("filter_org_hint"),
        }}
      />

      {(pending.error || pending.total > 0) && (
        <section className="space-y-3" aria-labelledby="wallet-debits-pending">
          <div>
            <h2
              id="wallet-debits-pending"
              className="flex items-center gap-2 text-base font-semibold text-foreground"
            >
              <Clock className="size-4 text-warning" aria-hidden="true" />
              {t("pending_section_title")}
              {pending.total > 0 && (
                <span className="text-sm font-normal text-muted-foreground">({pending.total})</span>
              )}
            </h2>
            <p className="text-sm text-muted-foreground">{t("pending_section_hint")}</p>
          </div>
          {pending.error ? (
            <ColocaAlert
              variant="destructive"
              icon={AlertCircle}
              text={t("pending_section_failed", { message: pending.error })}
              actions={
                <ColocaButton variant="default" size="sm" icon={RefreshCw} onClick={pending.reload}>
                  {t("action_retry")}
                </ColocaButton>
              }
            />
          ) : (
            <WalletDebitsList
              rows={pending.rows}
              isLoading={pending.isLoading}
              page={pending.page}
              total={pending.total}
              onPageChange={pending.setPage}
              onOpen={setOpened}
            />
          )}
        </section>
      )}

      {history.error ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={history.error}
          actions={
            <ColocaButton variant="default" size="sm" icon={RefreshCw} onClick={history.reload}>
              {t("action_retry")}
            </ColocaButton>
          }
        />
      ) : (
        <WalletDebitsList
          rows={history.rows}
          isLoading={history.isLoading}
          page={history.page}
          total={history.total}
          onPageChange={history.setPage}
          onOpen={setOpened}
        />
      )}

      <NewWalletDebitDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={reload}
        onUpdated={reload}
      />
      <WalletDebitDetailDrawer
        debit={opened}
        onClose={() => setOpened(null)}
        onUpdated={(debit) => {
          // An upload that ends after the drawer closed must not open it again.
          setOpened((current) => (current?.id === debit.id ? debit : current));
          reload();
        }}
      />
    </div>
  );
}
